import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecentProjectsTicker = () => {
  const [albums, setAlbums] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/albums/recent?limit=5`)
      .then((r) => {
        if (cancelled) return;
        setAlbums(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setAlbums([]);
      })
      .finally(() => !cancelled && setLoaded(true));
    return () => { cancelled = true; };
  }, []);

  // Graceful hide if no data (empty DB / API off)
  if (loaded && albums.length === 0) return null;
  if (!loaded) return null;

  // Duplicate the list so the marquee always has enough width
  const items = albums.length < 5 ? [...albums, ...albums, ...albums] : [...albums, ...albums];

  return (
    <section
      className="border-y border-white/10 bg-[#0B0B0D] py-16 lg:py-20 relative overflow-hidden"
      data-testid="home-recent-projects"
    >
      {/* Header row */}
      <div className="px-6 lg:px-10 mb-10 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-3 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#FF5722] animate-pulse" />
            Latest Off The Line
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
            Fresh <span className="text-[#FF5722]">Projects</span>
          </h2>
        </div>
        <Link
          to="/gallery"
          className="group inline-flex items-center gap-3 text-sm uppercase tracking-widest text-white/70 hover:text-white transition-colors"
          data-testid="recent-projects-view-all"
        >
          View Full Gallery
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </Link>
      </div>

      {/* Ticker */}
      <Marquee gradient={false} speed={35} pauseOnHover autoFill>
        {items.map((a, idx) => (
          <Link
            key={`${a.id}-${idx}`}
            to={`/gallery/${a.slug}`}
            className="group relative block w-[320px] md:w-[420px] lg:w-[500px] aspect-[4/3] mx-3 overflow-hidden border border-white/10 bg-black"
            data-testid={`recent-project-card-${a.slug}`}
          >
            <img
              src={a.cover_image_url}
              alt={a.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

            {/* Corner ticker dot */}
            <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/80">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5722]" />
              New
            </div>

            {/* Bottom label */}
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
              <div className="eyebrow mb-1 text-white/60">
                {a.item_count} {a.item_count === 1 ? "photo" : "photos"}
              </div>
              <div className="font-display text-2xl md:text-3xl tracking-tighter leading-none flex items-center gap-2">
                <span className="line-clamp-1">{a.title}</span>
                <ArrowUpRight className="w-5 h-5 text-[#FF5722] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all shrink-0" />
              </div>
            </div>
          </Link>
        ))}
      </Marquee>
    </section>
  );
};

export default RecentProjectsTicker;
